/** Предпросмотр фиксации: счётчики флагов (фильтры), таблица текущая → новая цена, галочки
 *  «Фиксировать» (по умолчанию стоят), выбор рецептуры у `multi_spec`, замечания (предупреждения —
 *  красным, допущения — справочно), подтверждение. Что запишется — `preview.ts` (правила бэка). */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  confirmFixation,
  type ConfirmResult,
  type FixationPreview,
  type PreviewFlag,
  type PreviewRow,
  type WarehouseRate,
} from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { FLAG_ORDER, FLAG_STYLE, jumpText, money, purchasePrice, rateOn, SOURCE_STYLE } from "./format";
import {
  chosenSpec,
  confirmPlan,
  EMPTY_SELECTION,
  excludeMissingSpec,
  filterRows,
  headerState,
  isChecked,
  newPrice,
  NOT_COMPUTED_HINT,
  notComputedReasons,
  okSpecs,
  rowJump,
  rowMode,
  setVisibleChecked,
  specChoiceValue,
  type HeaderState,
  type Selection,
} from "./preview";
import { NO_RATE_TEXT } from "./rate";
import { invalidatePrices, useRates } from "./queries";
import { Badge, CharacteristicLine, ErrorBox } from "./ui";

export function PreviewPanel({
  fixationId,
  preview,
  validFrom,
  onConfirmed,
}: {
  fixationId: number;
  preview: FixationPreview;
  /** с какой даты действуют новые цены фиксации — на неё берётся ставка склада */
  validFrom: string;
  onConfirmed: (r: ConfirmResult) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const rates = useRates();
  const [sel, setSel] = useState<Selection>(EMPTY_SELECTION);
  const [flag, setFlag] = useState<PreviewFlag | null>(null);
  const [q, setQ] = useState("");
  const rows = preview.rows;
  const plan = useMemo(() => confirmPlan(rows, sel), [rows, sel]);
  const shown = useMemo(() => filterRows(rows, flag, q), [rows, flag, q]);
  const header = headerState(shown, sel);

  const toggle = useCallback(
    (key: string) =>
      setSel((s) => {
        const excluded = new Set(s.excluded);
        if (excluded.has(key)) excluded.delete(key);
        else excluded.add(key);
        return { ...s, excluded };
      }),
    [],
  );
  const choose = useCallback(
    (key: string, ref: string) =>
      setSel((s) => {
        const specChoice = { ...s.specChoice };
        const excluded = new Set(s.excluded);
        if (ref) {
          specChoice[key] = ref;
          // выбор рецептуры = намерение зафиксировать: вернуть снятую галочку
          excluded.delete(key);
        } else delete specChoice[key];
        return { ...s, specChoice, excluded };
      }),
    [],
  );

  const m = useMutation({
    mutationFn: () => confirmFixation(fixationId, plan.body),
    onSuccess: async (r) => {
      onConfirmed(r);
      await invalidatePrices(qc);
    },
    // 409/422 и т.п.: фиксация могла измениться (отброшена, подтверждена) — перечитать, чтобы
    // устаревший предпросмотр и кнопка не висели
    onError: () => invalidatePrices(qc),
  });

  const blocked = plan.missingSpec.length > 0 || plan.checked === 0;

  return (
    <div className="space-y-3" data-testid="preview">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Фильтр по флагу">
        <FilterChip active={flag === null} onClick={() => setFlag(null)} label="все" count={rows.length} />
        {FLAG_ORDER.filter((f) => (preview.counts[f] ?? 0) > 0).map((f) => (
          <FilterChip
            key={f}
            active={flag === f}
            onClick={() => setFlag(flag === f ? null : f)}
            label={FLAG_STYLE[f].label}
            title={FLAG_STYLE[f].hint}
            count={preview.counts[f] ?? 0}
            cls={FLAG_STYLE[f].cls}
          />
        ))}
        <Input
          className="ml-auto h-8 w-64"
          placeholder="артикул, название"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Поиск в предпросмотре"
        />
      </div>
      {preview.assumptions.length > 0 && (
        <details className="rounded-md border px-3 py-2 text-xs text-muted-foreground">
          <summary className="cursor-pointer">Допущения расчёта ({preview.assumptions.length})</summary>
          <ul className="mt-1 list-disc pl-4">
            {preview.assumptions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </details>
      )}

      {rates.isError && (
        <ErrorBox error={rates.error} what="Ставки склада не загрузились — себестоимость не посчитана" />
      )}
      <div className="max-h-[60vh] overflow-auto rounded-md border">
        <table className="w-full text-sm" data-testid="preview-table">
          <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-2 py-2 font-medium">
                <span className="inline-flex items-center gap-1.5">
                  <HeaderCheckbox
                    state={header}
                    onChange={(checked) => setSel((s) => setVisibleChecked(shown, s, checked))}
                  />
                  Фиксировать
                </span>
              </th>
              <th className="px-2 py-2 font-medium">Артикул</th>
              <th className="px-2 py-2 font-medium">Название</th>
              <th
                className="px-2 py-2 text-right font-medium"
                title="себестоимость = себестоимость производства с НДС + склад производства (ставка на дату цены)"
              >
                Текущая → новая (себестоимость)
              </th>
              <th className="px-2 py-2 text-right font-medium">Скачок</th>
              <th className="px-2 py-2 font-medium">Флаг</th>
              <th className="px-2 py-2 font-medium">Замечания</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <PreviewTr
                key={r.key}
                row={r}
                excluded={sel.excluded.has(r.key)}
                choice={sel.specChoice[r.key]}
                onToggle={toggle}
                onChoose={choose}
                threshold={preview.jump_threshold_pct}
                rates={rates.data}
                validFrom={validFrom}
              />
            ))}
            {shown.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                  Строк нет.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {plan.missingSpec.length > 0 && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
        >
          Не выбрана рецептура у {plan.missingSpec.length} строк — выберите или снимите «Фиксировать».
          <Button size="sm" variant="outline" className="h-7" onClick={() => setSel((s) => excludeMissingSpec(rows, s))}>
            Не фиксировать их
          </Button>
        </div>
      )}
      {m.isError && <ErrorBox error={m.error} what="Подтверждение не прошло" />}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => m.mutate()} disabled={blocked || m.isPending} data-testid="confirm-btn">
          Зафиксировать {plan.checked} цен
        </Button>
        <span className="text-xs text-muted-foreground">
          не фиксировать: {plan.body.exclude.length}; рецептур выбрано: {Object.keys(plan.body.spec_choice).length}
        </span>
      </div>
    </div>
  );
}

/** Галочка шапки по видимым строкам: все / часть (indeterminate) / ни одной; доступных нет — выключена. */
function HeaderCheckbox({
  state,
  onChange,
}: {
  state: HeaderState;
  onChange: (checked: boolean) => void;
}): React.ReactElement {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "some";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label="Фиксировать все видимые"
      title={
        state === "none_available"
          ? "среди видимых строк нет доступных для фиксации"
          : "отметить / снять все видимые строки (с учётом фильтра и поиска)"
      }
      checked={state === "all"}
      disabled={state === "none_available"}
      onChange={() => onChange(state !== "all")}
    />
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
  cls,
  title,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  cls?: string;
  title?: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-pressed={active}
      title={title}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs font-medium",
        cls,
        active ? "ring-2 ring-primary" : "opacity-80 hover:opacity-100",
      )}
    >
      {label} <span className="tabular-nums">{count}</span>
    </button>
  );
}

const PreviewTr = memo(function PreviewTr({
  row,
  excluded,
  choice,
  onToggle,
  onChoose,
  threshold,
  rates,
  validFrom,
}: {
  row: PreviewRow;
  excluded: boolean;
  choice: string | undefined;
  onToggle: (key: string) => void;
  onChoose: (key: string, ref: string) => void;
  /** порог скачка, % (`jump_threshold_pct` предпросмотра) */
  threshold: number;
  /** ставки склада; нет — ещё грузятся / не загрузились */
  rates: WarehouseRate[] | undefined;
  validFrom: string;
}): React.ReactElement {
  const mode = rowMode(row);
  const sel: Selection = {
    excluded: excluded ? new Set([row.key]) : new Set(),
    specChoice: choice === undefined ? {} : { [row.key]: choice },
  };
  const checked = isChecked(row, sel);
  const spec = chosenSpec(row, sel);
  const next = newPrice(row, sel);
  const jump = rowJump(row, sel);
  // себестоимость = себестоимость производства + ставка склада на дату цены (текущей — её valid_from, новой — фиксации)
  const full = (gross: number, date: string): number | null =>
    rates ? purchasePrice(gross, rateOn(rates, date)) : null;
  const newRate = rates ? rateOn(rates, validFrom) : null;
  const noRate =
    !!rates && (newRate === null || (row.current !== null && rateOn(rates, row.current.valid_from) === null));
  const needsChoice = mode.kind === "needs_spec" && checked && spec === null;
  return (
    <tr
      className={cn("border-t align-top", !checked && "text-muted-foreground")}
      data-testid="preview-row"
      data-key={row.key}
    >
      <td className="px-2 py-1.5">
        <input
          type="checkbox"
          aria-label="Фиксировать"
          checked={checked}
          disabled={mode.kind === "locked"}
          title={mode.kind === "locked" ? mode.reason : "фиксировать эту цену"}
          onChange={() => onToggle(row.key)}
        />
      </td>
      <td className="px-2 py-1.5 font-medium">{row.article ?? "—"}</td>
      <td className="px-2 py-1.5">
        {row.name}
        <CharacteristicLine characteristicRef={row.characteristic_ref} name={row.characteristic_name} />
        {mode.kind === "needs_spec" && (
          <label className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            Рецептура:
            <select
              aria-label="Рецептура"
              className={cn(
                "h-7 max-w-64 rounded-md border bg-background px-1 text-xs text-foreground",
                needsChoice && "border-red-500",
              )}
              value={choice ?? ""}
              onChange={(e) => onChoose(row.key, e.target.value)}
            >
              <option value="">— выберите —</option>
              {okSpecs(row).map((s) => (
                <option key={specChoiceValue(s)} value={specChoiceValue(s)}>
                  {s.spec_name ?? s.spec_ref ?? "без спецификации"} · {money(s.price_gross)}
                </option>
              ))}
            </select>
          </label>
        )}
      </td>
      <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">
        <span className="text-muted-foreground">
          {row.current ? money(full(row.current.price_gross, row.current.valid_from)) : "нет"}
        </span>
        {" → "}
        <span className={cn(checked && "font-medium")}>{next === null ? "—" : money(full(next, validFrom))}</span>
        <div className="text-xs text-muted-foreground" data-testid="preview-cost">
          себестоимость {row.current ? money(row.current.price_gross) : "нет"} → {money(next)}
          {newRate && ` + склад ${money(newRate.rub_per_unit)}`}
        </div>
        {row.current && (
          <div className="text-xs text-muted-foreground">{SOURCE_STYLE[row.current.source].label}</div>
        )}
        {noRate && <div className="text-xs text-red-600 dark:text-red-400">{NO_RATE_TEXT}</div>}
      </td>
      <td
        className={cn(
          "whitespace-nowrap px-2 py-1.5 text-right tabular-nums",
          jump !== null && Math.abs(jump) > threshold && "text-orange-700 dark:text-orange-400",
        )}
      >
        {jumpText(jump)}
      </td>
      <td className="px-2 py-1.5">
        <Badge cls={FLAG_STYLE[row.flag].cls} title={FLAG_STYLE[row.flag].hint}>
          {FLAG_STYLE[row.flag].label}
        </Badge>
        {mode.kind === "locked" && row.flag !== "not_computed" && (
          <div className="mt-0.5 max-w-56 text-xs">{mode.reason}</div>
        )}
      </td>
      <td className="px-2 py-1.5 text-xs" data-testid="remarks">
        {row.flag === "not_computed" ? (
          <NotComputed reasons={notComputedReasons(row)} />
        ) : (
          mode.kind !== "locked" &&
          spec && <Remarks warnings={spec.warnings} assumptions={spec.assumptions} />
        )}
      </td>
    </tr>
  );
});

function NotComputed({ reasons }: { reasons: string[] }): React.ReactElement {
  return (
    <>
      {reasons.length > 0 && (
        <ul className="list-disc pl-4 text-red-700 dark:text-red-400" data-testid="reasons">
          {reasons.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      )}
      <div className="mt-0.5 text-muted-foreground">{NOT_COMPUTED_HINT}</div>
    </>
  );
}

function Remarks({ warnings, assumptions }: { warnings: string[]; assumptions: string[] }): React.ReactElement {
  return (
    <>
      {warnings.length > 0 && (
        <ul className="list-disc pl-4 text-red-700 dark:text-red-400" data-testid="warnings">
          {warnings.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      )}
      {assumptions.length > 0 && (
        <details className="text-muted-foreground" data-testid="assumptions">
          <summary className="cursor-pointer">допущения: {assumptions.length}</summary>
          <ul className="list-disc pl-4">
            {assumptions.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}
